export interface MapExportSampleGeometry {
  readonly county: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
  readonly state: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
  readonly zipCodePoints: readonly GeoJSON.Feature<GeoJSON.Point>[];
  readonly zipCodes: readonly GeoJSON.Feature<GeoJSON.MultiPolygon>[];
}

/**
 * Real GeoJSON, no decimation: county/state fully, zipCodes to the 34 real polygons in Johnson County.
 * zipCodePoints are the other 13 — PO boxes with no polygon, label-only ("Display Zip Points"). Neighboring
 * counties aren't drawn — the base map screenshot already shows them.
 */
// Typed directly against MapExportSampleGeometry, not inline in Object.freeze, so string literals narrow
// to their discriminant types instead of widening to `string`.
const sampleGeometry: MapExportSampleGeometry = {
  county: {
    geometry: {
      coordinates: [
        [
          [
            [-94.793039, 39.043642], [-94.806375, 39.045553], [-94.810538, 39.046645], [-94.821777, 39.051185],
            [-94.837637, 39.059409], [-94.842648, 39.060956], [-94.848732, 39.06158], [-94.856724, 39.061538],
            [-94.861922, 39.060977], [-94.870654, 39.058958], [-94.874699, 39.057848], [-94.878808, 39.05516],
            [-94.882331, 39.052109], [-94.883883, 39.049111], [-94.883596, 39.044912], [-94.884965, 39.039818],
            [-94.88819, 39.034017], [-94.890426, 39.026431], [-94.891138, 39.019243], [-94.891137, 39.014267],
            [-94.891813, 39.012416], [-94.893882, 39.008508], [-94.909048, 38.991186], [-94.91213, 38.987778],
            [-94.915385, 38.985619], [-94.919551, 38.98344], [-94.921442, 38.984159], [-94.921957, 38.984951],
            [-94.921839, 38.987479], [-94.916905, 38.995279], [-94.916679, 38.996759], [-94.917064, 38.998696],
            [-94.918036, 39.000579], [-94.920225, 39.002168], [-94.923336, 39.003053], [-94.926762, 39.00302],
            [-94.931026, 39.001738], [-94.93454, 38.997548], [-94.939278, 38.994072], [-94.943844, 38.987802],
            [-94.946997, 38.985423], [-94.949626, 38.98418], [-94.957095, 38.982261], [-94.964869, 38.982289],
            [-94.968238, 38.982923], [-94.969566, 38.983629], [-94.972297, 38.985397], [-94.975179, 38.988418],
            [-94.978226, 38.990784], [-94.97976, 38.991474], [-94.993794, 38.994574], [-95.000649, 38.998013],
            [-95.0036, 38.998873], [-95.011413, 39.000747], [-95.014041, 39.0008], [-95.021799, 38.999574],
            [-95.024636, 38.995846], [-95.026066, 38.993094], [-95.025931, 38.991449], [-95.024175, 38.987237],
            [-95.024509, 38.984111], [-95.025293, 38.982312], [-95.027846, 38.978865], [-95.02974, 38.977469],
            [-95.033267, 38.975489], [-95.036525, 38.974586], [-95.038934, 38.974184], [-95.042252, 38.974572],
            [-95.0497, 38.978178], [-95.056225, 38.982136], [-95.056568, 38.971906], [-95.055949, 38.920778],
            [-95.056191, 38.840586], [-95.056325, 38.767756], [-95.056423, 38.738597], [-94.968237, 38.738622],
            [-94.96374, 38.738495], [-94.94565, 38.738319], [-94.934876, 38.738425], [-94.930192, 38.738402],
            [-94.927141, 38.738408], [-94.908607, 38.73829], [-94.760314, 38.738263], [-94.609092, 38.738122],
            [-94.608876, 38.752681], [-94.607625, 38.827559], [-94.608034, 38.847207], [-94.608034, 38.850108],
            [-94.608033, 38.869604], [-94.608653, 38.869519], [-94.608034, 38.871629], [-94.607882, 38.883909],
            [-94.608, 38.912896], [-94.607951, 38.936881], [-94.608378, 38.978709], [-94.608122, 38.985616],
            [-94.607885, 39.000188], [-94.607568, 39.028343], [-94.607405, 39.033854], [-94.607371, 39.044034],
            [-94.638447, 39.044013], [-94.638638, 39.043915], [-94.638768, 39.043795], [-94.643994, 39.044116],
            [-94.649153, 39.044008], [-94.658924, 39.044026], [-94.723868, 39.043974], [-94.72845, 39.043962],
            [-94.733203, 39.043991], [-94.739391, 39.043818], [-94.758779, 39.043706], [-94.761056, 39.043633],
            [-94.788055, 39.043655], [-94.78917, 39.043495], [-94.793039, 39.043642],
          ],
        ],
      ],
      type: "MultiPolygon",
    },
    properties: {},
    type: "Feature",
  },
  state: {
    geometry: {
      coordinates: [
        [
          [-102.0517, 40.0031], [-101.6258, 40.0027], [-101.2866, 40.0026], [-100.9848, 40.0022], [-100.6916, 40.0022], [-100.5672, 40.0019],
          [-100.4471, 40.0018], [-100.1903, 40.0016], [-99.9909, 40.0015], [-99.7721, 40.0018], [-99.5812, 40.0019], [-99.4126, 40.0019],
          [-99.2353, 40.002], [-99.1135, 40.0022], [-98.9526, 40.0023], [-98.6931, 40.0024], [-98.5606, 40.0023], [-98.1793, 40.0025],
          [-98.0144, 40.0022], [-97.8328, 40.0019], [-97.6707, 40.002], [-97.4447, 40.002], [-97.2559, 40.0016], [-97.0308, 40.0013],
          [-96.8783, 40.0015], [-96.6224, 40.0012], [-96.5579, 40.001], [-96.4636, 40.001], [-96.1993, 40.0006], [-96.0517, 40.0007],
          [-95.788, 40.0005], [-95.4553, 40.0001], [-95.4147, 40], [-95.2748, 39.9721], [-95.2013, 39.9342], [-95.1795, 39.9001],
          [-95.1406, 39.8817], [-95.0321, 39.8683], [-94.9934, 39.8978], [-94.9273, 39.8803], [-94.9032, 39.8339], [-94.8809, 39.7973],
          [-94.9297, 39.7691], [-94.8839, 39.7702], [-94.8605, 39.7492], [-94.8993, 39.724], [-94.9637, 39.7403], [-94.9841, 39.6779],
          [-95.039, 39.6496], [-95.0464, 39.5996], [-95.0971, 39.5803], [-95.0968, 39.5279], [-95.0408, 39.4664], [-94.973, 39.4217],
          [-94.9382, 39.3871], [-94.889, 39.3924], [-94.899, 39.3624], [-94.9053, 39.312], [-94.8578, 39.2741], [-94.8278, 39.234],
          [-94.7838, 39.2072], [-94.7419, 39.1702], [-94.6603, 39.1681], [-94.596, 39.1577], [-94.6072, 39.0896], [-94.6075, 39.0464],
          [-94.6079, 39.0152], [-94.6083, 38.9787], [-94.608, 38.9368], [-94.608, 38.8838], [-94.6077, 38.8329], [-94.6089, 38.7672],
          [-94.6119, 38.6205], [-94.6126, 38.4958], [-94.6133, 38.3999], [-94.6128, 38.2914], [-94.6127, 38.2176], [-94.6141, 38.0659],
          [-94.6146, 37.9441], [-94.616, 37.8631], [-94.6178, 37.7297], [-94.6175, 37.6372], [-94.6172, 37.5536], [-94.6172, 37.4605],
          [-94.6176, 37.3673], [-94.6181, 37.2281], [-94.6181, 37.1337], [-94.6181, 37.0859], [-94.618, 37.0309], [-94.7372, 36.9987],
          [-94.8498, 36.9989], [-95.0735, 36.9995], [-95.3312, 36.9994], [-95.6159, 36.9994], [-95.7104, 36.9994], [-95.8669, 36.9993],
          [-96.1412, 36.999], [-96.2764, 36.9993], [-96.6245, 36.999], [-96.8675, 36.9992], [-97.0397, 36.999], [-97.2558, 36.999],
          [-97.5459, 36.9987], [-97.7834, 36.999], [-98.1282, 36.9981], [-98.3541, 36.998], [-98.7919, 36.9993], [-99.0497, 36.9992],
          [-99.5004, 36.9996], [-99.7748, 37.0008], [-100.1875, 37.0021], [-100.6298, 37], [-100.8682, 36.9986], [-101.0665, 36.9979],
          [-101.4139, 36.996], [-101.8266, 36.9941], [-102.0419, 37.0308], [-102.0418, 37.3095], [-102.0419, 37.558], [-102.0432, 37.8679],
          [-102.0446, 38.2687], [-102.0452, 38.5816], [-102.0454, 38.7835], [-102.0492, 39.4036], [-102.0509, 39.7418], [-102.0517, 40.0031],
        ],
      ],
      type: "Polygon",
    },
    properties: {},
    type: "Feature",
  },
  zipCodePoints: [
    { geometry: { coordinates: [-94.648067, 39.022564], type: "Point" }, properties: { zipCode: "66222" }, type: "Feature" },
    { geometry: { coordinates: [-94.686328, 38.902258], type: "Point" }, properties: { zipCode: "66225" }, type: "Feature" },
    { geometry: { coordinates: [-94.667408, 39.02644], type: "Point" }, properties: { zipCode: "66276" }, type: "Feature" },
    { geometry: { coordinates: [-94.659431, 38.91849], type: "Point" }, properties: { zipCode: "66251" }, type: "Feature" },
    { geometry: { coordinates: [-94.759823, 38.878855], type: "Point" }, properties: { zipCode: "66063" }, type: "Feature" },
    { geometry: { coordinates: [-94.672627, 38.854668], type: "Point" }, properties: { zipCode: "66283" }, type: "Feature" },
    { geometry: { coordinates: [-94.841356, 39.009541], type: "Point" }, properties: { zipCode: "66286" }, type: "Feature" },
    { geometry: { coordinates: [-94.76088, 38.938281], type: "Point" }, properties: { zipCode: "66250" }, type: "Feature" },
    { geometry: { coordinates: [-94.818231, 38.882265], type: "Point" }, properties: { zipCode: "66051" }, type: "Feature" },
    { geometry: { coordinates: [-94.965558, 38.978663], type: "Point" }, properties: { zipCode: "66019" }, type: "Feature" },
    { geometry: { coordinates: [-94.665626, 39.019892], type: "Point" }, properties: { zipCode: "66201" }, type: "Feature" },
    { geometry: { coordinates: [-94.686261, 38.952595], type: "Point" }, properties: { zipCode: "66282" }, type: "Feature" },
    { geometry: { coordinates: [-94.728325, 38.968955], type: "Point" }, properties: { zipCode: "66285" }, type: "Feature" },
  ],
  zipCodes: [
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.723711, 38.810986], [-94.733401, 38.810977], [-94.734801, 38.810104], [-94.737226, 38.810847],
              [-94.74238, 38.81088], [-94.742275, 38.792671], [-94.736648, 38.791031], [-94.736238, 38.791024],
              [-94.736154, 38.791517], [-94.73266, 38.791251], [-94.730478, 38.789694], [-94.729271, 38.789839],
              [-94.727669, 38.79183], [-94.726103, 38.792239], [-94.724113, 38.791534], [-94.722625, 38.791723],
              [-94.721941, 38.790993], [-94.720719, 38.792291], [-94.721775, 38.793067], [-94.720651, 38.793834],
              [-94.71864, 38.793858], [-94.718389, 38.792992], [-94.716399, 38.792855], [-94.709297, 38.794383],
              [-94.707436, 38.792697], [-94.704913, 38.793413], [-94.698933, 38.787282], [-94.699012, 38.786579],
              [-94.700477, 38.787071], [-94.702378, 38.787032], [-94.705071, 38.785881], [-94.705096, 38.78399],
              [-94.705924, 38.781819], [-94.705339, 38.780731], [-94.704964, 38.774739], [-94.723361, 38.774629],
              [-94.723254, 38.752917], [-94.732774, 38.752851], [-94.732792, 38.767404], [-94.741789, 38.767327],
              [-94.741712, 38.752785], [-94.760299, 38.752748], [-94.760314, 38.738263], [-94.760222, 38.733595],
              [-94.760221, 38.709161], [-94.741771, 38.709144], [-94.741761, 38.694665], [-94.740889, 38.694605],
              [-94.740838, 38.686988], [-94.740865, 38.679672], [-94.722224, 38.679587], [-94.722293, 38.672077],
              [-94.711103, 38.671977], [-94.710376, 38.670036], [-94.7094, 38.668942], [-94.709222, 38.667624],
              [-94.706582, 38.667001], [-94.707333, 38.666026], [-94.707899, 38.664026], [-94.708504, 38.663759],
              [-94.709437, 38.662178], [-94.709486, 38.66147], [-94.708859, 38.660668], [-94.708848, 38.661782],
              [-94.708189, 38.66136], [-94.706744, 38.662418], [-94.704378, 38.66128], [-94.703277, 38.66219],
              [-94.702023, 38.662058], [-94.700685, 38.662999], [-94.699326, 38.662983], [-94.699114, 38.663929],
              [-94.696964, 38.663494], [-94.696147, 38.664146], [-94.693908, 38.664776], [-94.693177, 38.665777],
              [-94.693622, 38.667056], [-94.692671, 38.667897], [-94.689831, 38.666936], [-94.686767, 38.667659],
              [-94.685092, 38.667231], [-94.685297, 38.672063], [-94.684603, 38.676289], [-94.68484, 38.679049],
              [-94.694333, 38.679023], [-94.694282, 38.694541], [-94.66799, 38.694577], [-94.667584, 38.695959],
              [-94.667614, 38.709119], [-94.648989, 38.709071], [-94.649043, 38.694559], [-94.642505, 38.69455],
              [-94.641768, 38.69504], [-94.641757, 38.694548], [-94.610083, 38.694516], [-94.609368, 38.729374],
              [-94.609228, 38.733595], [-94.609092, 38.738122], [-94.608876, 38.752681], [-94.66565, 38.752678],
              [-94.663902, 38.75399], [-94.65933, 38.762495], [-94.665177, 38.764146], [-94.667726, 38.766313],
              [-94.667737, 38.767227], [-94.686284, 38.767244], [-94.686347, 38.810215], [-94.686425, 38.810821],
              [-94.686839, 38.810906], [-94.705091, 38.810947], [-94.705038, 38.803638], [-94.712298, 38.8036],
              [-94.723685, 38.803624], [-94.723711, 38.810986],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66013" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.909048, 38.991185], [-94.91213, 38.987777], [-94.915385, 38.985618], [-94.919551, 38.983439],
              [-94.921442, 38.984158], [-94.921957, 38.984951], [-94.921839, 38.98748], [-94.916905, 38.995278],
              [-94.916679, 38.996759], [-94.917064, 38.998695], [-94.918036, 39.000578], [-94.920224, 39.002168],
              [-94.923336, 39.003052], [-94.926762, 39.003019], [-94.931026, 39.001737], [-94.93454, 38.997548],
              [-94.939278, 38.994072], [-94.943844, 38.987802], [-94.946997, 38.985423], [-94.949626, 38.98418],
              [-94.957095, 38.982261], [-94.964869, 38.982289], [-94.968238, 38.982923], [-94.969566, 38.983628],
              [-94.972297, 38.985396], [-94.975179, 38.988418], [-94.978226, 38.990784], [-94.97976, 38.991474],
              [-94.993794, 38.994573], [-95.000649, 38.998012], [-95.0036, 38.998872], [-95.011413, 39.000748],
              [-95.014041, 39.000799], [-95.021799, 38.999573], [-95.024636, 38.995845], [-95.026066, 38.993094],
              [-95.02593, 38.991448], [-95.024175, 38.987236], [-95.024509, 38.984111], [-95.025293, 38.982311],
              [-95.027846, 38.978865], [-95.02974, 38.977469], [-95.033267, 38.975488], [-95.036525, 38.974585],
              [-95.036363, 38.973239], [-95.037627, 38.97215], [-95.037873, 38.970635], [-95.03848, 38.969996],
              [-95.037994, 38.969037], [-95.037812, 38.964423], [-95.035969, 38.963035], [-95.033757, 38.959836],
              [-95.03401, 38.956866], [-95.026552, 38.95684], [-95.025084, 38.957304], [-95.02219, 38.959281],
              [-95.022788, 38.959243], [-95.022521, 38.964555], [-95.021751, 38.964646], [-95.019312, 38.966323],
              [-95.019228, 38.965786], [-95.019363, 38.942746], [-95.020498, 38.942269], [-95.029361, 38.942384],
              [-95.029652, 38.941197], [-95.030841, 38.9399], [-95.030813, 38.937193], [-95.033188, 38.937177],
              [-95.034898, 38.935652], [-95.034785, 38.927786], [-95.034197, 38.927155], [-95.030001, 38.926948],
              [-95.03481, 38.926857], [-95.034632, 38.912462], [-95.027992, 38.903878], [-95.026964, 38.89886],
              [-95.027813, 38.890445], [-95.030684, 38.884232], [-94.992314, 38.884351], [-94.992452, 38.898536],
              [-94.991496, 38.898745], [-94.992888, 38.899921], [-94.992828, 38.901503], [-94.992154, 38.904258],
              [-94.990606, 38.904256], [-94.989956, 38.904919], [-94.990465, 38.905203], [-94.989959, 38.905656],
              [-94.990618, 38.906682], [-94.989076, 38.906974], [-94.989183, 38.907661], [-94.987641, 38.907294],
              [-94.989047, 38.909192], [-94.98706, 38.908891], [-94.984857, 38.912221], [-94.98536, 38.914155],
              [-94.983819, 38.915027], [-94.984429, 38.920201], [-94.983857, 38.921431], [-94.98539, 38.922439],
              [-94.984992, 38.923263], [-94.985565, 38.924597], [-94.985507, 38.925062], [-94.984392, 38.925788],
              [-94.985234, 38.928491], [-94.984164, 38.9291], [-94.983835, 38.930175], [-94.973385, 38.927448],
              [-94.945864, 38.927418], [-94.945899, 38.949096], [-94.93311, 38.948873], [-94.932753, 38.94825],
              [-94.931763, 38.949207], [-94.928424, 38.948753], [-94.928176, 38.949675], [-94.926456, 38.950439],
              [-94.926368, 38.951743], [-94.924964, 38.952231], [-94.923891, 38.951341], [-94.923072, 38.951491],
              [-94.92233, 38.953003], [-94.922569, 38.953971], [-94.919889, 38.954555], [-94.919077, 38.953941],
              [-94.918046, 38.953941], [-94.918048, 38.955031], [-94.916398, 38.955264], [-94.916365, 38.952703],
              [-94.911167, 38.952612], [-94.911067, 38.949943], [-94.908652, 38.949911], [-94.908703, 38.952306],
              [-94.907048, 38.950013], [-94.905955, 38.949531], [-94.90615, 38.947524], [-94.905335, 38.948044],
              [-94.905762, 38.948581], [-94.905384, 38.949767], [-94.90418, 38.949458], [-94.905521, 38.950202],
              [-94.90524, 38.950479], [-94.904484, 38.951728], [-94.906588, 38.952607], [-94.908586, 38.955056],
              [-94.908748, 38.964841], [-94.90918, 38.96485], [-94.909174, 38.965991], [-94.911267, 38.964943],
              [-94.912322, 38.963639], [-94.913298, 38.963838], [-94.912447, 38.964878], [-94.912333, 38.967263],
              [-94.91039, 38.967228], [-94.910525, 38.968623], [-94.918555, 38.969114], [-94.918224, 38.971487],
              [-94.917433, 38.971803], [-94.916745, 38.973359], [-94.908806, 38.973601], [-94.908582, 38.979508],
              [-94.908049, 38.980941], [-94.908884, 38.98525], [-94.909561, 38.985446], [-94.908935, 38.987159],
              [-94.90906, 38.988989], [-94.909048, 38.991185],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66018" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-95.001369, 38.796768], [-95.00142, 38.811235], [-95.019046, 38.811301], [-95.01894, 38.840401],
              [-95.056191, 38.840586], [-95.074777, 38.840582], [-95.074815, 38.782175], [-95.093388, 38.782207],
              [-95.093407, 38.767853], [-95.063563, 38.767805], [-95.062218, 38.766455], [-95.058082, 38.767765],
              [-95.056325, 38.767756], [-95.056423, 38.738597], [-95.05644, 38.733595], [-95.056464, 38.724049],
              [-95.047762, 38.72397], [-95.046055, 38.720112], [-95.046468, 38.719136], [-95.046282, 38.718058],
              [-95.044971, 38.71672], [-95.044168, 38.716474], [-95.044539, 38.715582], [-95.044399, 38.714104],
              [-95.044963, 38.713643], [-95.044303, 38.712725], [-95.04433, 38.709629], [-95.04721, 38.70945],
              [-95.047203, 38.706531], [-95.0443, 38.705566], [-95.043357, 38.704255], [-95.040621, 38.702828],
              [-95.038631, 38.702264], [-95.037869, 38.702734], [-95.037321, 38.702005], [-95.034724, 38.701936],
              [-95.032178, 38.700861], [-95.030971, 38.700903], [-95.030063, 38.70163], [-95.029654, 38.702794],
              [-95.029119, 38.702635], [-95.028696, 38.702286], [-95.028707, 38.694893], [-95.011973, 38.694885],
              [-94.993254, 38.694856], [-94.993459, 38.680333], [-94.948566, 38.680005], [-94.947404, 38.679664],
              [-94.946666, 38.678486], [-94.947175, 38.680711], [-94.947432, 38.687495], [-94.945825, 38.691126],
              [-94.945598, 38.694434], [-94.946234, 38.693249], [-94.946677, 38.693162], [-94.950131, 38.698168],
              [-94.94999, 38.699081], [-94.951588, 38.700106], [-94.952004, 38.701707], [-94.953049, 38.701892],
              [-94.954346, 38.701472], [-94.956973, 38.702102], [-94.958475, 38.703695], [-94.958238, 38.704733],
              [-94.958888, 38.705721], [-94.962505, 38.709123], [-94.964594, 38.70956], [-94.96431, 38.711085],
              [-94.966153, 38.711473], [-94.973056, 38.711464], [-94.972374, 38.712203], [-94.971149, 38.712304],
              [-94.971, 38.713245], [-94.969625, 38.713318], [-94.969142, 38.713753], [-94.970313, 38.714188],
              [-94.970443, 38.716348], [-94.969154, 38.717397], [-94.969458, 38.719883], [-94.967834, 38.719613],
              [-94.967381, 38.719896], [-94.968581, 38.720958], [-94.968633, 38.722173], [-94.967793, 38.722326],
              [-94.96756, 38.721475], [-94.965484, 38.720832], [-94.964266, 38.72146], [-94.962743, 38.721223],
              [-94.962887, 38.722326], [-94.961362, 38.725929], [-94.962112, 38.728223], [-94.962783, 38.72881],
              [-94.964941, 38.728673], [-94.966507, 38.727787], [-94.967929, 38.729221], [-94.968264, 38.7278],
              [-94.969671, 38.727562], [-94.969447, 38.729059], [-94.97095, 38.729407], [-94.971698, 38.730522],
              [-94.974471, 38.732785], [-94.975967, 38.733426], [-94.974536, 38.733247], [-94.971819, 38.731287],
              [-94.972172, 38.732789], [-94.970757, 38.732687], [-94.970062, 38.733595], [-94.969987, 38.734183],
              [-94.971342, 38.734691], [-94.971423, 38.736341], [-94.970528, 38.737229], [-94.968674, 38.737939],
              [-94.968237, 38.738622], [-94.96374, 38.738495], [-94.964037, 38.753039], [-94.961676, 38.753315],
              [-94.960147, 38.753008], [-94.945777, 38.75302], [-94.927123, 38.752917], [-94.927079, 38.767449],
              [-94.945901, 38.767504], [-94.945786, 38.788953], [-94.952536, 38.788705], [-94.952638, 38.782078],
              [-94.964116, 38.782188], [-94.964037, 38.789213], [-94.958113, 38.792198], [-94.958128, 38.794893],
              [-94.958467, 38.794987], [-94.958904, 38.796704], [-94.973735, 38.796739], [-94.973579, 38.811147],
              [-94.992263, 38.811178], [-94.992164, 38.796756], [-95.001369, 38.796768],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66021" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-95.042252, 38.974572], [-95.0497, 38.978178], [-95.056225, 38.982136], [-95.061293, 38.985245],
              [-95.066557, 38.986653], [-95.072845, 38.986232], [-95.080705, 38.984115], [-95.084768, 38.982365],
              [-95.086695, 38.980226], [-95.086276, 38.978503], [-95.086885, 38.976629], [-95.086806, 38.974333],
              [-95.084792, 38.972395], [-95.082147, 38.971255], [-95.076668, 38.966956], [-95.07446, 38.964375],
              [-95.074567, 38.96212], [-95.07645, 38.959837], [-95.080998, 38.956847], [-95.086773, 38.954679],
              [-95.088393, 38.954477], [-95.092531, 38.956459], [-95.100856, 38.956219], [-95.104541, 38.959069],
              [-95.106034, 38.961858], [-95.107529, 38.966674], [-95.109611, 38.96888], [-95.115169, 38.970045],
              [-95.11823, 38.969664], [-95.119644, 38.969952], [-95.121888, 38.969933], [-95.125057, 38.967941],
              [-95.126896, 38.967705], [-95.139118, 38.969341], [-95.143884, 38.971618], [-95.14644, 38.97237],
              [-95.150453, 38.97579], [-95.150107, 38.974484], [-95.150791, 38.97479], [-95.150035, 38.973801],
              [-95.149349, 38.969109], [-95.148826, 38.968678], [-95.148864, 38.939311], [-95.149511, 38.936742],
              [-95.149201, 38.934355], [-95.152884, 38.935024], [-95.166921, 38.935382], [-95.167638, 38.906217],
              [-95.165055, 38.907102], [-95.162782, 38.906234], [-95.148953, 38.906126], [-95.148972, 38.898794],
              [-95.1303, 38.898758], [-95.130238, 38.884198], [-95.149053, 38.884218], [-95.149122, 38.869654],
              [-95.167882, 38.86974], [-95.167699, 38.844226], [-95.167239, 38.840516], [-95.093165, 38.840599],
              [-95.093253, 38.811426], [-95.095941, 38.811423], [-95.096951, 38.81013], [-95.097154, 38.808338],
              [-95.096525, 38.80651], [-95.093193, 38.80537], [-95.093274, 38.796828], [-95.093388, 38.782207],
              [-95.074815, 38.782175], [-95.074777, 38.840582], [-95.056191, 38.840586], [-95.01894, 38.840401],
              [-95.018926, 38.869509], [-95.017737, 38.869511], [-95.015722, 38.872606], [-95.033398, 38.872604],
              [-95.033589, 38.881767], [-95.031337, 38.882971], [-95.030684, 38.884232], [-95.027813, 38.890445],
              [-95.026964, 38.89886], [-95.027992, 38.903878], [-95.034632, 38.912462], [-95.03481, 38.926857],
              [-95.030001, 38.926948], [-95.034197, 38.927155], [-95.034785, 38.927786], [-95.034898, 38.935652],
              [-95.033188, 38.937177], [-95.030813, 38.937193], [-95.030841, 38.9399], [-95.029652, 38.941197],
              [-95.029361, 38.942384], [-95.020498, 38.94227], [-95.019363, 38.942746], [-95.019228, 38.965786],
              [-95.019312, 38.966324], [-95.021751, 38.964647], [-95.022521, 38.964556], [-95.022788, 38.959243],
              [-95.02219, 38.959282], [-95.025084, 38.957304], [-95.026552, 38.956841], [-95.03401, 38.956866],
              [-95.033757, 38.959837], [-95.035969, 38.963036], [-95.037812, 38.964424], [-95.037994, 38.969037],
              [-95.03848, 38.969997], [-95.037873, 38.970636], [-95.037627, 38.972149], [-95.036363, 38.973239],
              [-95.036525, 38.974586], [-95.038934, 38.974184], [-95.042252, 38.974572],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66025" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.908967, 38.840011], [-94.918745, 38.839722], [-94.922801, 38.840166], [-94.923122, 38.840875],
              [-94.923035, 38.845789], [-94.924467, 38.847309], [-94.924464, 38.850065], [-94.923084, 38.85138],
              [-94.923017, 38.852653], [-94.927536, 38.85277], [-94.927522, 38.854669], [-94.908967, 38.854548],
              [-94.908885, 38.869119], [-94.938011, 38.86928], [-94.938017, 38.870821], [-94.938903, 38.871482],
              [-94.938978, 38.872191], [-94.940381, 38.872416], [-94.946961, 38.871376], [-94.946578, 38.869342],
              [-95.017737, 38.869512], [-95.018926, 38.869508], [-95.01894, 38.840401], [-95.019046, 38.8113],
              [-95.00142, 38.811236], [-95.001369, 38.796768], [-94.992164, 38.796756], [-94.992263, 38.811178],
              [-94.973579, 38.811147], [-94.973735, 38.796739], [-94.958904, 38.796705], [-94.958467, 38.794987],
              [-94.958128, 38.794894], [-94.958113, 38.792197], [-94.964037, 38.789213], [-94.964116, 38.782188],
              [-94.952638, 38.782078], [-94.952536, 38.788705], [-94.945786, 38.788953], [-94.945901, 38.767504],
              [-94.927078, 38.767449], [-94.927123, 38.752918], [-94.945777, 38.753021], [-94.94565, 38.738319],
              [-94.934876, 38.738425], [-94.935352, 38.743414], [-94.934693, 38.744317], [-94.933411, 38.744614],
              [-94.932165, 38.744194], [-94.929871, 38.742183], [-94.927136, 38.741526], [-94.927141, 38.738408],
              [-94.908606, 38.73829], [-94.908635, 38.752864], [-94.890087, 38.752912], [-94.890069, 38.767493],
              [-94.879683, 38.767542], [-94.878721, 38.769796], [-94.878832, 38.771226], [-94.880222, 38.772303],
              [-94.880581, 38.773106], [-94.880879, 38.781861], [-94.871525, 38.781852], [-94.871452, 38.79651],
              [-94.853326, 38.796497], [-94.853308, 38.797545], [-94.853314, 38.810983], [-94.871405, 38.810973],
              [-94.871367, 38.816758], [-94.877979, 38.813778], [-94.878682, 38.813744], [-94.878705, 38.814251],
              [-94.880797, 38.813507], [-94.880826, 38.818567], [-94.881204, 38.818561], [-94.885581, 38.815825],
              [-94.890466, 38.814266], [-94.890437, 38.814773], [-94.899992, 38.813081], [-94.902061, 38.817992],
              [-94.908974, 38.817911], [-94.908967, 38.840011],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66030" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.890437, 38.814773], [-94.888552, 38.815288], [-94.889506, 38.815691], [-94.888569, 38.825417],
              [-94.881093, 38.825485], [-94.8809, 38.825951], [-94.88086, 38.839678], [-94.885337, 38.840019],
              [-94.885597, 38.847294], [-94.895099, 38.84786], [-94.90427, 38.847294], [-94.904322, 38.841846],
              [-94.908962, 38.841632], [-94.908967, 38.840011], [-94.908974, 38.817911], [-94.902061, 38.817992],
              [-94.899992, 38.813081], [-94.890437, 38.814773],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66031" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.90524, 38.950479], [-94.905521, 38.950202], [-94.90418, 38.949458], [-94.905384, 38.949767],
              [-94.905762, 38.948581], [-94.905335, 38.948044], [-94.90615, 38.947524], [-94.905955, 38.949531],
              [-94.907048, 38.950013], [-94.908703, 38.952306], [-94.908652, 38.949911], [-94.911067, 38.949943],
              [-94.911167, 38.952612], [-94.916365, 38.952703], [-94.916398, 38.955264], [-94.918048, 38.955031],
              [-94.918046, 38.953941], [-94.919077, 38.953941], [-94.919889, 38.954555], [-94.922569, 38.953971],
              [-94.92233, 38.953003], [-94.923072, 38.951491], [-94.923891, 38.951341], [-94.924964, 38.952231],
              [-94.926368, 38.951743], [-94.926456, 38.950439], [-94.928176, 38.949675], [-94.928424, 38.948753],
              [-94.931763, 38.949207], [-94.932753, 38.94825], [-94.93311, 38.948873], [-94.945899, 38.949096],
              [-94.945864, 38.927418], [-94.973385, 38.927448], [-94.983835, 38.930175], [-94.984164, 38.9291],
              [-94.985234, 38.928491], [-94.984392, 38.925788], [-94.985507, 38.925062], [-94.985565, 38.924597],
              [-94.984992, 38.923263], [-94.98539, 38.922439], [-94.983857, 38.921431], [-94.984429, 38.920201],
              [-94.983819, 38.915027], [-94.98536, 38.914155], [-94.984857, 38.912221], [-94.98706, 38.908891],
              [-94.989047, 38.909192], [-94.987641, 38.907294], [-94.989183, 38.907661], [-94.989076, 38.906974],
              [-94.990618, 38.906682], [-94.989959, 38.905656], [-94.990465, 38.905203], [-94.989956, 38.904919],
              [-94.990606, 38.904256], [-94.992154, 38.904258], [-94.992828, 38.901503], [-94.992888, 38.899921],
              [-94.991496, 38.898745], [-94.992452, 38.898536], [-94.992314, 38.884351], [-95.030684, 38.884232],
              [-95.031337, 38.882971], [-95.033589, 38.881766], [-95.033397, 38.872603], [-95.015722, 38.872605],
              [-95.017737, 38.869512], [-94.946578, 38.869342], [-94.946961, 38.871376], [-94.940381, 38.872416],
              [-94.938978, 38.872191], [-94.938903, 38.871482], [-94.938017, 38.870821], [-94.938011, 38.86928],
              [-94.908885, 38.869119], [-94.908967, 38.854548], [-94.927522, 38.854669], [-94.927536, 38.85277],
              [-94.923017, 38.852653], [-94.923084, 38.85138], [-94.924464, 38.850065], [-94.924467, 38.847309],
              [-94.923035, 38.845789], [-94.923122, 38.840875], [-94.922801, 38.840166], [-94.918745, 38.839722],
              [-94.908967, 38.840011], [-94.908962, 38.841632], [-94.904322, 38.841846], [-94.90427, 38.847294],
              [-94.895099, 38.84786], [-94.885597, 38.847294], [-94.885337, 38.840019], [-94.88086, 38.839678],
              [-94.8809, 38.825951], [-94.881093, 38.825485], [-94.888569, 38.825417], [-94.889506, 38.815691],
              [-94.888552, 38.815288], [-94.890437, 38.814773], [-94.890466, 38.814266], [-94.885581, 38.815825],
              [-94.881204, 38.818561], [-94.880826, 38.818567], [-94.880797, 38.813507], [-94.878705, 38.814251],
              [-94.878682, 38.813744], [-94.877979, 38.813778], [-94.871367, 38.816758], [-94.864821, 38.820703],
              [-94.853597, 38.829463], [-94.839221, 38.838656], [-94.839003, 38.838453], [-94.836473, 38.840003],
              [-94.833897, 38.83997], [-94.833853, 38.841493], [-94.828122, 38.845203], [-94.800117, 38.867736],
              [-94.798649, 38.868305], [-94.797392, 38.869634], [-94.797139, 38.86912], [-94.794765, 38.871203],
              [-94.79442, 38.872148], [-94.795133, 38.87231], [-94.794127, 38.874204], [-94.787431, 38.894884],
              [-94.783387, 38.900782], [-94.779447, 38.905313], [-94.769539, 38.91415], [-94.770541, 38.914674],
              [-94.772735, 38.914942], [-94.772941, 38.916374], [-94.779503, 38.916393], [-94.779682, 38.917655],
              [-94.788825, 38.91718], [-94.788745, 38.927296], [-94.793317, 38.927266], [-94.793381, 38.934613],
              [-94.784139, 38.934613], [-94.784136, 38.938304], [-94.788826, 38.938304], [-94.788826, 38.939083],
              [-94.789884, 38.939183], [-94.790641, 38.941401], [-94.797588, 38.940709], [-94.801652, 38.941461],
              [-94.806958, 38.941506], [-94.807296, 38.941883], [-94.816066, 38.942612], [-94.816061, 38.942033],
              [-94.826696, 38.941574], [-94.844378, 38.941649], [-94.846552, 38.941642], [-94.848132, 38.941135],
              [-94.865133, 38.94142], [-94.868844, 38.942249], [-94.883371, 38.947104], [-94.888073, 38.947686],
              [-94.898345, 38.947455], [-94.90171, 38.948395], [-94.90524, 38.950479],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66061" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.742456, 38.915633], [-94.742427, 38.916488], [-94.743892, 38.916688], [-94.744429, 38.920118],
              [-94.742356, 38.920362], [-94.745474, 38.921266], [-94.748826, 38.920556], [-94.749536, 38.9208],
              [-94.749001, 38.921995], [-94.751615, 38.922363], [-94.751578, 38.920518], [-94.754483, 38.92052],
              [-94.754664, 38.921761], [-94.756298, 38.920103], [-94.760889, 38.920085], [-94.760886, 38.923514],
              [-94.763818, 38.920122], [-94.764193, 38.920253], [-94.770541, 38.914674], [-94.769539, 38.91415],
              [-94.779447, 38.905313], [-94.783387, 38.900782], [-94.787431, 38.894884], [-94.794127, 38.874204],
              [-94.795133, 38.87231], [-94.79442, 38.872148], [-94.794765, 38.871203], [-94.797139, 38.86912],
              [-94.797392, 38.869634], [-94.798649, 38.868305], [-94.800117, 38.867736], [-94.828122, 38.845203],
              [-94.833853, 38.841493], [-94.833897, 38.83997], [-94.836473, 38.840003], [-94.839003, 38.838453],
              [-94.839221, 38.838656], [-94.853597, 38.829463], [-94.864821, 38.820703], [-94.871367, 38.816758],
              [-94.871405, 38.810973], [-94.853314, 38.810983], [-94.853308, 38.797545], [-94.853326, 38.796497],
              [-94.871452, 38.79651], [-94.871525, 38.781852], [-94.838915, 38.78185], [-94.839049, 38.789109],
              [-94.83436, 38.789095], [-94.834394, 38.792813], [-94.815566, 38.792936], [-94.815542, 38.781896],
              [-94.806461, 38.78192], [-94.806485, 38.789202], [-94.797276, 38.788987], [-94.797273, 38.796474],
              [-94.781426, 38.796517], [-94.779457, 38.796505], [-94.779247, 38.781911], [-94.742093, 38.781871],
              [-94.742275, 38.792671], [-94.74238, 38.81088], [-94.761046, 38.811002], [-94.761119, 38.818452],
              [-94.749144, 38.818114], [-94.749536, 38.819831], [-94.751439, 38.823042], [-94.751164, 38.824291],
              [-94.749431, 38.825591], [-94.742516, 38.82569], [-94.742432, 38.840181], [-94.735749, 38.840168],
              [-94.735943, 38.847813], [-94.733173, 38.847785], [-94.73319, 38.854675], [-94.71441, 38.854625],
              [-94.714416, 38.857725], [-94.718854, 38.857659], [-94.719076, 38.858304], [-94.719062, 38.861997],
              [-94.714466, 38.861956], [-94.714447, 38.864526], [-94.714447, 38.865502], [-94.71905, 38.865517],
              [-94.71906, 38.869149], [-94.742272, 38.869138], [-94.742423, 38.883725], [-94.742472, 38.912919],
              [-94.742456, 38.915633],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66062" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.779457, 38.796506], [-94.781426, 38.796516], [-94.797273, 38.796473], [-94.797277, 38.788987],
              [-94.806485, 38.789203], [-94.806461, 38.781921], [-94.815542, 38.781897], [-94.815566, 38.792935],
              [-94.834394, 38.792813], [-94.83436, 38.789095], [-94.839049, 38.789109], [-94.838915, 38.781851],
              [-94.871525, 38.781853], [-94.880879, 38.781861], [-94.880581, 38.773105], [-94.880222, 38.772303],
              [-94.878832, 38.771227], [-94.878722, 38.769797], [-94.879683, 38.767543], [-94.890069, 38.767493],
              [-94.890087, 38.752913], [-94.908636, 38.752864], [-94.908607, 38.73829], [-94.927141, 38.738408],
              [-94.927136, 38.741526], [-94.929871, 38.742182], [-94.932165, 38.744194], [-94.933411, 38.744615],
              [-94.934693, 38.744318], [-94.935352, 38.743415], [-94.934876, 38.738425], [-94.94565, 38.738319],
              [-94.945777, 38.75302], [-94.960147, 38.753008], [-94.961676, 38.753315], [-94.964037, 38.753039],
              [-94.96374, 38.738495], [-94.968237, 38.738621], [-94.968674, 38.737939], [-94.970528, 38.737229],
              [-94.971423, 38.736341], [-94.971342, 38.734691], [-94.969987, 38.734183], [-94.970062, 38.733595],
              [-94.970757, 38.732687], [-94.972172, 38.732789], [-94.971819, 38.731287], [-94.974536, 38.733247],
              [-94.975967, 38.733426], [-94.974471, 38.732785], [-94.971698, 38.730522], [-94.97095, 38.729407],
              [-94.969447, 38.729059], [-94.969671, 38.727562], [-94.968264, 38.7278], [-94.967929, 38.729221],
              [-94.966507, 38.727787], [-94.964941, 38.728673], [-94.962783, 38.72881], [-94.962112, 38.728223],
              [-94.961362, 38.725929], [-94.962887, 38.722326], [-94.962743, 38.721223], [-94.964266, 38.72146],
              [-94.965484, 38.720832], [-94.96756, 38.721475], [-94.967793, 38.722326], [-94.968633, 38.722173],
              [-94.968581, 38.720958], [-94.967381, 38.719896], [-94.967834, 38.719613], [-94.969458, 38.719883],
              [-94.969154, 38.717397], [-94.970443, 38.716348], [-94.970313, 38.714188], [-94.969142, 38.713753],
              [-94.969625, 38.713318], [-94.971, 38.713245], [-94.971149, 38.712304], [-94.972374, 38.712203],
              [-94.973056, 38.711464], [-94.966153, 38.711473], [-94.96431, 38.711085], [-94.964594, 38.70956],
              [-94.962505, 38.709123], [-94.958888, 38.705721], [-94.958238, 38.704733], [-94.958475, 38.703695],
              [-94.956973, 38.702102], [-94.954346, 38.701472], [-94.953049, 38.701892], [-94.952004, 38.701707],
              [-94.951588, 38.700106], [-94.94999, 38.699081], [-94.950131, 38.698168], [-94.946677, 38.693162],
              [-94.946234, 38.693249], [-94.945598, 38.694434], [-94.945825, 38.691126], [-94.947432, 38.687495],
              [-94.947175, 38.680711], [-94.946666, 38.678486], [-94.939446, 38.67965], [-94.921345, 38.679088],
              [-94.918978, 38.679733], [-94.918906, 38.687075], [-94.906848, 38.687405], [-94.906003, 38.688132],
              [-94.905762, 38.689109], [-94.904157, 38.689658], [-94.902956, 38.691016], [-94.902889, 38.692118],
              [-94.904209, 38.694615], [-94.902282, 38.694185], [-94.903077, 38.692643], [-94.90183, 38.691221],
              [-94.902069, 38.689765], [-94.901195, 38.689454], [-94.898813, 38.691716], [-94.891701, 38.686601],
              [-94.890929, 38.688216], [-94.889343, 38.689931], [-94.887929, 38.68932], [-94.883994, 38.690555],
              [-94.881144, 38.689141], [-94.881892, 38.690552], [-94.886135, 38.692496], [-94.883203, 38.691725],
              [-94.882652, 38.692523], [-94.883362, 38.694163], [-94.883239, 38.694786], [-94.880474, 38.695476],
              [-94.882488, 38.695908], [-94.883733, 38.697812], [-94.882577, 38.699655], [-94.879483, 38.702314],
              [-94.878076, 38.705098], [-94.8784, 38.706246], [-94.877408, 38.706644], [-94.876229, 38.706066],
              [-94.875316, 38.706245], [-94.874666, 38.707834], [-94.871809, 38.709323], [-94.868041, 38.709289],
              [-94.868033, 38.70464], [-94.86878, 38.703399], [-94.871271, 38.7016], [-94.871418, 38.697083],
              [-94.866547, 38.696906], [-94.866447, 38.694798], [-94.843598, 38.694743], [-94.843879, 38.692589],
              [-94.845413, 38.688975], [-94.845649, 38.677336], [-94.84945, 38.671482], [-94.849605, 38.66837],
              [-94.850794, 38.665108], [-94.836905, 38.665047], [-94.836088, 38.668272], [-94.836131, 38.677463],
              [-94.836801, 38.679646], [-94.80594, 38.67914], [-94.787476, 38.679769], [-94.768966, 38.67968],
              [-94.768612, 38.694555], [-94.741761, 38.694665], [-94.741771, 38.709144], [-94.760221, 38.709161],
              [-94.760222, 38.733595], [-94.760314, 38.738263], [-94.760299, 38.752748], [-94.741712, 38.752785],
              [-94.741789, 38.767327], [-94.732792, 38.767404], [-94.732774, 38.752851], [-94.723254, 38.752917],
              [-94.723361, 38.774629], [-94.704964, 38.774739], [-94.705339, 38.780731], [-94.705924, 38.781819],
              [-94.705096, 38.78399], [-94.705071, 38.785881], [-94.702378, 38.787032], [-94.700477, 38.787071],
              [-94.699012, 38.786579], [-94.698933, 38.787282], [-94.704913, 38.793413], [-94.707436, 38.792697],
              [-94.709297, 38.794383], [-94.716399, 38.792855], [-94.718389, 38.792992], [-94.71864, 38.793858],
              [-94.720651, 38.793834], [-94.721775, 38.793067], [-94.720719, 38.792291], [-94.721941, 38.790993],
              [-94.722625, 38.791723], [-94.724113, 38.791534], [-94.726103, 38.792239], [-94.727669, 38.79183],
              [-94.729271, 38.789839], [-94.730478, 38.789694], [-94.73266, 38.791251], [-94.736154, 38.791517],
              [-94.736238, 38.791024], [-94.736648, 38.791031], [-94.742275, 38.792671], [-94.742093, 38.781871],
              [-94.779247, 38.781912], [-94.779457, 38.796506],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66083" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.608876, 38.75268], [-94.607625, 38.82756], [-94.608034, 38.847207], [-94.608034, 38.850108],
              [-94.609142, 38.848699], [-94.608738, 38.847942], [-94.60954, 38.843276], [-94.608841, 38.841645],
              [-94.608886, 38.839661], [-94.60968, 38.838906], [-94.61107, 38.8388], [-94.610164, 38.840182],
              [-94.610238, 38.841126], [-94.61193, 38.841592], [-94.611886, 38.83719], [-94.614642, 38.837755],
              [-94.615642, 38.837057], [-94.617752, 38.836591], [-94.619512, 38.837196], [-94.619269, 38.837747],
              [-94.617752, 38.837316], [-94.616636, 38.837994], [-94.616378, 38.838964], [-94.6167, 38.840278],
              [-94.649153, 38.840029], [-94.676935, 38.839829], [-94.677701, 38.829961], [-94.680477, 38.831208],
              [-94.683656, 38.83062], [-94.683979, 38.829243], [-94.682756, 38.828027], [-94.683291, 38.827946],
              [-94.686228, 38.828316], [-94.686222, 38.839946], [-94.705096, 38.839996], [-94.705204, 38.838048],
              [-94.705279, 38.825443], [-94.691059, 38.825466], [-94.691115, 38.81934], [-94.694349, 38.816728],
              [-94.694293, 38.815886], [-94.693841, 38.815256], [-94.692143, 38.814625], [-94.691905, 38.813328],
              [-94.690515, 38.81306], [-94.68949, 38.813779], [-94.688523, 38.81289], [-94.68762, 38.81278],
              [-94.687562, 38.812317], [-94.686571, 38.812213], [-94.686839, 38.810905], [-94.686425, 38.810821],
              [-94.686347, 38.810214], [-94.686284, 38.767244], [-94.667737, 38.767227], [-94.667726, 38.766313],
              [-94.665176, 38.764146], [-94.65933, 38.762495], [-94.663902, 38.753991], [-94.66565, 38.752677],
              [-94.608876, 38.75268],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66085" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.649153, 39.044008], [-94.658924, 39.044026], [-94.665153, 39.042425], [-94.670862, 39.041809],
              [-94.676972, 39.040105], [-94.680319, 39.038663], [-94.685256, 39.035277], [-94.688923, 39.030853],
              [-94.690748, 39.026206], [-94.692738, 39.017984], [-94.693798, 39.00767], [-94.690536, 39.007646],
              [-94.690489, 39.005946], [-94.689568, 39.00573], [-94.688293, 39.005954], [-94.68826, 39.00766],
              [-94.667543, 39.007667], [-94.667432, 39.005232], [-94.66537, 39.00524], [-94.665355, 39.007657],
              [-94.649054, 39.007613], [-94.649017, 39.009913], [-94.646204, 39.010121], [-94.646401, 39.010784],
              [-94.649011, 39.010767], [-94.648998, 39.013642], [-94.647972, 39.013921], [-94.648037, 39.014891],
              [-94.649012, 39.014885], [-94.649153, 39.044008],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66202" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.658924, 39.044026], [-94.723868, 39.043973], [-94.72362, 38.99288], [-94.70507, 38.992952],
              [-94.697707, 38.993098], [-94.696834, 38.994088], [-94.694949, 38.9981], [-94.693798, 39.00767],
              [-94.692738, 39.017984], [-94.690748, 39.026206], [-94.688923, 39.030853], [-94.685256, 39.035277],
              [-94.680319, 39.038663], [-94.676972, 39.040105], [-94.670862, 39.041809], [-94.665153, 39.042425],
              [-94.658924, 39.044026],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66203" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.649054, 39.007613], [-94.665355, 39.007657], [-94.66537, 39.00524], [-94.667432, 39.005232],
              [-94.667543, 39.007667], [-94.68826, 39.00766], [-94.688293, 39.005954], [-94.689568, 39.00573],
              [-94.690489, 39.005946], [-94.690536, 39.007646], [-94.693798, 39.00767], [-94.694949, 38.9981],
              [-94.696834, 38.994088], [-94.697707, 38.993098], [-94.70507, 38.992952], [-94.705057, 38.988283],
              [-94.705631, 38.985655], [-94.705116, 38.985702], [-94.704952, 38.984161], [-94.705055, 38.978372],
              [-94.700351, 38.978402], [-94.700303, 38.980078], [-94.699455, 38.980257], [-94.698311, 38.979124],
              [-94.696101, 38.978461], [-94.679392, 38.978497], [-94.67925, 38.977971], [-94.676874, 38.979756],
              [-94.676854, 38.978947], [-94.675573, 38.978519], [-94.658301, 38.97856], [-94.658366, 38.99303],
              [-94.649126, 38.993129], [-94.649069, 38.998461], [-94.650235, 38.998467], [-94.65026, 39.000332],
              [-94.651395, 39.00033], [-94.651392, 39.003075], [-94.651381, 39.003979], [-94.649068, 39.003976],
              [-94.649054, 39.007613],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66204" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.643994, 39.044116], [-94.649153, 39.044008], [-94.649012, 39.014885], [-94.648037, 39.014891],
              [-94.621655, 39.015105], [-94.620863, 39.015514], [-94.619009, 39.018657], [-94.617098, 39.019399],
              [-94.616629, 39.020591], [-94.617634, 39.022], [-94.616688, 39.02418], [-94.612705, 39.025873],
              [-94.61153, 39.027559], [-94.609931, 39.028203], [-94.61038, 39.029505], [-94.610037, 39.031003],
              [-94.608869, 39.031539], [-94.608511, 39.033507], [-94.607405, 39.033854], [-94.607371, 39.044034],
              [-94.638447, 39.044013], [-94.63899, 39.044015], [-94.638768, 39.043795], [-94.643994, 39.044116],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66205" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.607951, 38.936881], [-94.608378, 38.978709], [-94.608122, 38.985616], [-94.613081, 38.984192],
              [-94.619553, 38.984139], [-94.623073, 38.983555], [-94.626152, 38.982386], [-94.625778, 38.978711],
              [-94.630319, 38.9787], [-94.630234, 38.933051], [-94.630141, 38.931859], [-94.624259, 38.932173],
              [-94.62241, 38.931519], [-94.619887, 38.931406], [-94.6196, 38.932179], [-94.619949, 38.93324],
              [-94.607951, 38.936881],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66206" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.630234, 38.933051], [-94.630319, 38.9787], [-94.658301, 38.97856], [-94.658311, 38.953025],
              [-94.65696, 38.947266], [-94.657922, 38.942524], [-94.658244, 38.934784], [-94.64885, 38.934784],
              [-94.648954, 38.938249], [-94.644943, 38.937661], [-94.643624, 38.936285], [-94.639693, 38.935311],
              [-94.639539, 38.932624], [-94.630234, 38.933051],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66207" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.607405, 39.033854], [-94.608511, 39.033506], [-94.608869, 39.031539], [-94.610036, 39.031003],
              [-94.61038, 39.029505], [-94.609931, 39.028202], [-94.61153, 39.027558], [-94.612705, 39.025874],
              [-94.616687, 39.02418], [-94.617634, 39.022], [-94.616629, 39.020591], [-94.617098, 39.0194],
              [-94.619009, 39.018657], [-94.620863, 39.015514], [-94.621654, 39.015105], [-94.648037, 39.014891],
              [-94.647972, 39.013921], [-94.648998, 39.013642], [-94.649011, 39.010767], [-94.646401, 39.010784],
              [-94.646204, 39.010121], [-94.649017, 39.009913], [-94.649054, 39.007613], [-94.649068, 39.003976],
              [-94.651381, 39.003979], [-94.651392, 39.003075], [-94.651395, 39.00033], [-94.65026, 39.000332],
              [-94.650235, 38.998467], [-94.649069, 38.998461], [-94.649126, 38.993129], [-94.658366, 38.99303],
              [-94.658301, 38.97856], [-94.630319, 38.9787], [-94.625778, 38.978711], [-94.626152, 38.982386],
              [-94.623073, 38.983555], [-94.619553, 38.984139], [-94.613081, 38.984192], [-94.608122, 38.985616],
              [-94.607885, 39.000188], [-94.607567, 39.028343], [-94.607405, 39.033854],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66208" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.608, 38.912896], [-94.667656, 38.913014], [-94.667806, 38.883831], [-94.649302, 38.883797],
              [-94.607882, 38.883909], [-94.608, 38.912896],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66209" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.723517, 38.93528], [-94.737152, 38.934825], [-94.742175, 38.936232], [-94.741998, 38.928002],
              [-94.742456, 38.915633], [-94.742472, 38.912919], [-94.667656, 38.913014], [-94.668466, 38.914644],
              [-94.667255, 38.920201], [-94.667697, 38.928647], [-94.667435, 38.934238], [-94.670167, 38.93412],
              [-94.671973, 38.933387], [-94.672026, 38.933013], [-94.680271, 38.934377], [-94.705097, 38.935312],
              [-94.723517, 38.93528],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66210" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.630234, 38.933051], [-94.639539, 38.932624], [-94.639693, 38.935311], [-94.643624, 38.936285],
              [-94.644943, 38.937661], [-94.648954, 38.938249], [-94.64885, 38.934784], [-94.658244, 38.934784],
              [-94.667435, 38.934238], [-94.667697, 38.928647], [-94.667255, 38.920201], [-94.668466, 38.914644],
              [-94.667656, 38.913014], [-94.608, 38.912896], [-94.607951, 38.936881], [-94.619949, 38.93324],
              [-94.6196, 38.932179], [-94.619887, 38.931406], [-94.62241, 38.931519], [-94.624259, 38.932173],
              [-94.630141, 38.931859], [-94.630234, 38.933051],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66211" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.658301, 38.97856], [-94.675573, 38.978519], [-94.676854, 38.978947], [-94.676874, 38.979756],
              [-94.67925, 38.977971], [-94.679392, 38.978497], [-94.696101, 38.978461], [-94.698311, 38.979124],
              [-94.699455, 38.980257], [-94.700303, 38.980078], [-94.700351, 38.978402], [-94.705055, 38.978372],
              [-94.704894, 38.945841], [-94.703028, 38.943475], [-94.700308, 38.943293], [-94.699668, 38.942664],
              [-94.699793, 38.942086], [-94.705281, 38.942079], [-94.705097, 38.935312], [-94.680271, 38.934377],
              [-94.672026, 38.933013], [-94.671973, 38.933387], [-94.670167, 38.93412], [-94.667435, 38.934238],
              [-94.658244, 38.934784], [-94.657922, 38.942524], [-94.65696, 38.947266], [-94.658311, 38.953025],
              [-94.658301, 38.97856],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66212" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.667806, 38.883831], [-94.667656, 38.913014], [-94.742472, 38.912919], [-94.742423, 38.883725],
              [-94.686407, 38.88382], [-94.667806, 38.883831],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66213" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.70507, 38.992952], [-94.72362, 38.99288], [-94.723579, 38.9856], [-94.720743, 38.985625],
              [-94.72074, 38.983878], [-94.723576, 38.983855], [-94.723568, 38.971017], [-94.719061, 38.971029],
              [-94.723529, 38.966313], [-94.723517, 38.93528], [-94.705097, 38.935312], [-94.705281, 38.942079],
              [-94.699793, 38.942086], [-94.699668, 38.942664], [-94.700308, 38.943293], [-94.703028, 38.943475],
              [-94.704894, 38.945841], [-94.705055, 38.978372], [-94.704952, 38.984161], [-94.705116, 38.985702],
              [-94.705631, 38.985655], [-94.705057, 38.988283], [-94.70507, 38.992952],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66214" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.723517, 38.93528], [-94.723529, 38.966313], [-94.719061, 38.971029], [-94.723568, 38.971017],
              [-94.723576, 38.983855], [-94.72074, 38.983878], [-94.720743, 38.985625], [-94.723579, 38.9856],
              [-94.761069, 38.985594], [-94.760919, 38.928154], [-94.760751, 38.927392], [-94.757537, 38.927428],
              [-94.760886, 38.923514], [-94.760889, 38.920085], [-94.756298, 38.920103], [-94.754664, 38.921761],
              [-94.754483, 38.92052], [-94.751578, 38.920518], [-94.751615, 38.922363], [-94.749001, 38.921995],
              [-94.749536, 38.9208], [-94.748826, 38.920556], [-94.745474, 38.921266], [-94.742356, 38.920362],
              [-94.744429, 38.920118], [-94.743892, 38.916688], [-94.742427, 38.916488], [-94.742456, 38.915633],
              [-94.741998, 38.928002], [-94.742175, 38.936232], [-94.737152, 38.934825], [-94.723517, 38.93528],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66215" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.728964, 39.044686], [-94.733087, 39.045633], [-94.733557, 39.045409], [-94.733341, 39.044748],
              [-94.739391, 39.043818], [-94.758779, 39.043706], [-94.758752, 39.040041], [-94.760964, 39.040444],
              [-94.760839, 39.036529], [-94.75187, 39.036539], [-94.751882, 39.035564], [-94.752424, 39.035571],
              [-94.752492, 39.03496], [-94.751387, 39.033125], [-94.756618, 39.032899], [-94.756599, 39.029294],
              [-94.761291, 39.028539], [-94.76127, 39.021995], [-94.75769, 39.021999], [-94.76011, 39.020966],
              [-94.761325, 39.016067], [-94.761069, 38.985594], [-94.723579, 38.985601], [-94.72362, 38.99288],
              [-94.723868, 39.043974], [-94.72845, 39.043962], [-94.728964, 39.044686],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66216" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.761071, 39.047111], [-94.766866, 39.050511], [-94.768413, 39.050819], [-94.773021, 39.048776],
              [-94.78917, 39.043495], [-94.793039, 39.043642], [-94.80049, 39.041691], [-94.801674, 39.042684],
              [-94.802713, 39.042912], [-94.807345, 39.041404], [-94.809005, 39.040403], [-94.807182, 39.039433],
              [-94.806774, 39.038694], [-94.80489, 39.038171], [-94.801478, 39.035332], [-94.800871, 39.033162],
              [-94.799914, 39.032811], [-94.798612, 39.031244], [-94.797537, 39.030694], [-94.795505, 39.030838],
              [-94.79504, 39.028428], [-94.792395, 39.025664], [-94.7926, 39.02403], [-94.801167, 39.023822],
              [-94.801784, 39.024141], [-94.801886, 39.024772], [-94.803286, 39.023296], [-94.801833, 39.023206],
              [-94.801601, 39.02181], [-94.797444, 39.021906], [-94.797465, 39.014347], [-94.796773, 39.008634],
              [-94.79756, 39.008457], [-94.797796, 39.007382], [-94.797517, 38.988919], [-94.79594, 38.988767],
              [-94.794543, 38.987949], [-94.793884, 38.986837], [-94.793961, 38.985389], [-94.761069, 38.985594],
              [-94.761325, 39.016067], [-94.76011, 39.020966], [-94.75769, 39.021999], [-94.76127, 39.021995],
              [-94.761291, 39.028539], [-94.756599, 39.029294], [-94.756618, 39.032899], [-94.751387, 39.033125],
              [-94.752492, 39.03496], [-94.752424, 39.035571], [-94.751882, 39.035564], [-94.75187, 39.036539],
              [-94.760839, 39.036529], [-94.760964, 39.040444], [-94.758752, 39.040041], [-94.758779, 39.043706],
              [-94.761056, 39.043633], [-94.761071, 39.047111],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66217" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.809005, 39.040403], [-94.819078, 39.045052], [-94.833728, 39.048643], [-94.833575, 39.046983],
              [-94.834009, 39.046177], [-94.834787, 39.046182], [-94.834277, 39.043536], [-94.834878, 39.043547],
              [-94.835885, 39.042635], [-94.835498, 39.041531], [-94.834536, 39.04155], [-94.834188, 39.040324],
              [-94.832729, 39.040081], [-94.832654, 39.03928], [-94.834818, 39.039497], [-94.83473, 39.029011],
              [-94.839439, 39.028986], [-94.839451, 38.99989], [-94.839383, 38.999094], [-94.841931, 38.999117],
              [-94.842124, 38.994429], [-94.844036, 38.994405], [-94.844109, 38.98711], [-94.843415, 38.986289],
              [-94.844077, 38.985225], [-94.825465, 38.985141], [-94.825545, 38.977969], [-94.818854, 38.977604],
              [-94.818727, 38.979578], [-94.817107, 38.984997], [-94.809291, 38.985812], [-94.793961, 38.985389],
              [-94.793883, 38.986837], [-94.794543, 38.987949], [-94.79594, 38.988768], [-94.797517, 38.988918],
              [-94.797796, 39.007382], [-94.79756, 39.008456], [-94.796773, 39.008633], [-94.797464, 39.014346],
              [-94.797444, 39.021906], [-94.8016, 39.02181], [-94.801833, 39.023205], [-94.803286, 39.023296],
              [-94.801886, 39.024771], [-94.801784, 39.02414], [-94.801167, 39.023823], [-94.7926, 39.02403],
              [-94.792394, 39.025664], [-94.79504, 39.028428], [-94.795505, 39.030838], [-94.797537, 39.030694],
              [-94.798612, 39.031243], [-94.799914, 39.032811], [-94.80087, 39.033162], [-94.801478, 39.035332],
              [-94.80489, 39.038171], [-94.806773, 39.038694], [-94.807182, 39.039434], [-94.809005, 39.040403],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66218" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.793961, 38.985389], [-94.809291, 38.985812], [-94.817107, 38.984997], [-94.818727, 38.979578],
              [-94.818854, 38.977604], [-94.818767, 38.974103], [-94.814516, 38.971055], [-94.812535, 38.970735],
              [-94.797708, 38.970969], [-94.795499, 38.962265], [-94.797405, 38.961044], [-94.798228, 38.959259],
              [-94.798315, 38.952931], [-94.796946, 38.950013], [-94.794528, 38.9471], [-94.792876, 38.943571],
              [-94.793791, 38.943392], [-94.803974, 38.94561], [-94.807296, 38.941883], [-94.806958, 38.941506],
              [-94.801652, 38.941461], [-94.797588, 38.940709], [-94.790641, 38.941401], [-94.789884, 38.939183],
              [-94.788826, 38.939083], [-94.788826, 38.938304], [-94.784136, 38.938304], [-94.784139, 38.934613],
              [-94.793381, 38.934613], [-94.793317, 38.927266], [-94.788745, 38.927296], [-94.788825, 38.91718],
              [-94.779682, 38.917655], [-94.779503, 38.916393], [-94.772941, 38.916374], [-94.772735, 38.914942],
              [-94.770541, 38.914674], [-94.764193, 38.920253], [-94.763818, 38.920122], [-94.760886, 38.923514],
              [-94.757537, 38.927428], [-94.760751, 38.927392], [-94.760919, 38.928154], [-94.761069, 38.985594],
              [-94.793961, 38.985389],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66219" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.818854, 38.977604], [-94.825545, 38.977969], [-94.825465, 38.985141], [-94.844077, 38.985225],
              [-94.844376, 38.971806], [-94.844064, 38.964746], [-94.843649, 38.96319], [-94.844741, 38.961174],
              [-94.844079, 38.959599], [-94.843979, 38.95655], [-94.844307, 38.952526], [-94.843817, 38.943028],
              [-94.844378, 38.941649], [-94.826696, 38.941574], [-94.816061, 38.942033], [-94.816066, 38.942612],
              [-94.807296, 38.941883], [-94.803974, 38.94561], [-94.793791, 38.943392], [-94.792876, 38.943571],
              [-94.794528, 38.9471], [-94.796946, 38.950013], [-94.798315, 38.952931], [-94.798228, 38.959259],
              [-94.797405, 38.961044], [-94.795499, 38.962265], [-94.797708, 38.970969], [-94.812535, 38.970735],
              [-94.814516, 38.971055], [-94.818767, 38.974103], [-94.818854, 38.977604],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66220" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.686222, 38.839946], [-94.686407, 38.88382], [-94.742423, 38.883725], [-94.742272, 38.869138],
              [-94.71906, 38.869149], [-94.71905, 38.865517], [-94.714447, 38.865502], [-94.714447, 38.864526],
              [-94.714466, 38.861956], [-94.719062, 38.861997], [-94.719076, 38.858304], [-94.718854, 38.857659],
              [-94.714416, 38.857725], [-94.71441, 38.854625], [-94.73319, 38.854675], [-94.733173, 38.847785],
              [-94.735943, 38.847813], [-94.735749, 38.840168], [-94.742432, 38.840181], [-94.742516, 38.82569],
              [-94.749431, 38.825591], [-94.751164, 38.824291], [-94.751439, 38.823042], [-94.749536, 38.819831],
              [-94.749144, 38.818114], [-94.761119, 38.818452], [-94.761046, 38.811002], [-94.74238, 38.81088],
              [-94.737226, 38.810846], [-94.734801, 38.810104], [-94.733401, 38.810977], [-94.723711, 38.810987],
              [-94.723685, 38.803624], [-94.712298, 38.8036], [-94.705038, 38.803637], [-94.705091, 38.810946],
              [-94.686839, 38.810905], [-94.686571, 38.812213], [-94.687562, 38.812317], [-94.68762, 38.81278],
              [-94.688523, 38.81289], [-94.68949, 38.813779], [-94.690515, 38.81306], [-94.691905, 38.813328],
              [-94.692143, 38.814625], [-94.693841, 38.815256], [-94.694293, 38.815886], [-94.694349, 38.816728],
              [-94.691115, 38.81934], [-94.691059, 38.825466], [-94.705279, 38.825443], [-94.705204, 38.838048],
              [-94.705096, 38.839996], [-94.686222, 38.839946],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66221" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.667806, 38.883831], [-94.686407, 38.88382], [-94.686222, 38.839946], [-94.686228, 38.828316],
              [-94.683291, 38.827946], [-94.682756, 38.828027], [-94.683979, 38.829243], [-94.683656, 38.83062],
              [-94.680477, 38.831208], [-94.677701, 38.829961], [-94.676935, 38.839829], [-94.649153, 38.840029],
              [-94.649302, 38.883797], [-94.667806, 38.883831],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66223" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.608034, 38.850108], [-94.608033, 38.869605], [-94.608653, 38.869519], [-94.608034, 38.871628],
              [-94.607882, 38.883909], [-94.649302, 38.883797], [-94.649153, 38.840029], [-94.6167, 38.840278],
              [-94.616378, 38.838964], [-94.616636, 38.837994], [-94.617752, 38.837316], [-94.619269, 38.837747],
              [-94.619512, 38.837196], [-94.617752, 38.836591], [-94.615642, 38.837057], [-94.614642, 38.837755],
              [-94.611886, 38.83719], [-94.61193, 38.841592], [-94.610238, 38.841126], [-94.610164, 38.840182],
              [-94.61107, 38.8388], [-94.60968, 38.838906], [-94.608886, 38.839661], [-94.608841, 38.841645],
              [-94.60954, 38.843276], [-94.608738, 38.847942], [-94.609142, 38.848699], [-94.608034, 38.850108],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66224" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.793038, 39.043643], [-94.806375, 39.045552], [-94.810538, 39.046644], [-94.821777, 39.051185],
              [-94.837637, 39.05941], [-94.842648, 39.060955], [-94.848732, 39.06158], [-94.856724, 39.061538],
              [-94.861922, 39.060977], [-94.870654, 39.058958], [-94.874699, 39.057848], [-94.878807, 39.05516],
              [-94.882331, 39.052109], [-94.883883, 39.049112], [-94.883596, 39.044913], [-94.884965, 39.039818],
              [-94.88819, 39.034016], [-94.890425, 39.026431], [-94.891138, 39.019243], [-94.891137, 39.014267],
              [-94.891813, 39.012416], [-94.893882, 39.008508], [-94.909048, 38.991185], [-94.90906, 38.988989],
              [-94.905126, 38.99282], [-94.899673, 38.999205], [-94.881895, 39.000128], [-94.844273, 38.999888],
              [-94.842977, 39.000378], [-94.839451, 38.99989], [-94.839439, 39.028986], [-94.83473, 39.029011],
              [-94.834818, 39.039497], [-94.832654, 39.03928], [-94.832729, 39.040081], [-94.834188, 39.040324],
              [-94.834536, 39.04155], [-94.835498, 39.041531], [-94.835885, 39.042635], [-94.834878, 39.043547],
              [-94.834277, 39.043536], [-94.834787, 39.046182], [-94.834009, 39.046177], [-94.833575, 39.046983],
              [-94.833728, 39.048643], [-94.819078, 39.045052], [-94.809005, 39.040403], [-94.807344, 39.041404],
              [-94.802713, 39.042913], [-94.801673, 39.042683], [-94.80049, 39.04169], [-94.793038, 39.043643],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66226" },
      type: "Feature",
    },
    {
      geometry: {
        coordinates: [
          [
            [
              [-94.839451, 38.99989], [-94.842977, 39.000378], [-94.844273, 38.999888], [-94.881895, 39.000128],
              [-94.899673, 38.999205], [-94.905126, 38.99282], [-94.90906, 38.988989], [-94.908935, 38.987159],
              [-94.909561, 38.985446], [-94.908884, 38.98525], [-94.908049, 38.980941], [-94.908582, 38.979508],
              [-94.908806, 38.973601], [-94.916745, 38.973359], [-94.917433, 38.971803], [-94.918224, 38.971487],
              [-94.918555, 38.969114], [-94.910525, 38.968623], [-94.91039, 38.967228], [-94.912333, 38.967263],
              [-94.912447, 38.964878], [-94.913298, 38.963838], [-94.912322, 38.963639], [-94.911267, 38.964943],
              [-94.909174, 38.965991], [-94.90918, 38.96485], [-94.908748, 38.964841], [-94.908586, 38.955056],
              [-94.906588, 38.952607], [-94.904484, 38.951728], [-94.90524, 38.950479], [-94.90171, 38.948395],
              [-94.898345, 38.947455], [-94.888073, 38.947686], [-94.883371, 38.947104], [-94.868844, 38.942249],
              [-94.865133, 38.94142], [-94.848132, 38.941135], [-94.846552, 38.941642], [-94.844378, 38.941649],
              [-94.843817, 38.943028], [-94.844307, 38.952526], [-94.843979, 38.95655], [-94.844079, 38.959599],
              [-94.844741, 38.961174], [-94.843649, 38.96319], [-94.844064, 38.964746], [-94.844376, 38.971806],
              [-94.844077, 38.985225], [-94.843415, 38.986289], [-94.844109, 38.98711], [-94.844036, 38.994405],
              [-94.842124, 38.994429], [-94.841931, 38.999117], [-94.839383, 38.999094], [-94.839451, 38.99989],
            ],
          ],
        ],
        type: "MultiPolygon",
      },
      properties: { zipCode: "66227" },
      type: "Feature",
    },
  ],
};

export const MAP_EXPORT_SAMPLE_GEOMETRY: MapExportSampleGeometry = Object.freeze(sampleGeometry);
